/**
 * Ground locomotion: run, stop, turnaround, and the crouch speed cap. Speed
 * only changes through acceleration; the start impulse is the one flagged
 * exception so the first frame of input already moves the character.
 */

import type { EventBuffer } from "../events";
import { abs, moveToward, sign } from "../fastmath";
import type { SimState } from "../state";
import type { CompiledTuning } from "../tuning";
import { DT, Ev, type InputFrame, S } from "../types";
import { setState } from "./transitions";

export function tickGrounded(s: SimState, input: InputFrame, t: CompiledTuning, ev: EventBuffer): void {
  const mx = input.moveX;
  const dir = sign(mx);
  const forwardLean = input.tiltX * s.facing > 0 ? input.tiltX * s.facing : 0;
  const backLean = input.tiltX * s.facing < 0 ? -input.tiltX * s.facing : 0;

  // Slide: a tucked body at speed. The stick does not accelerate, the speed decays (faster with the
  // torso leaned back, the power slide), and a ceiling keeps the slide going after Tuck is released.
  const tucked = input.tuck || s.tuckFloor > 0;
  const speed0 = abs(s.vx);
  // A press armed for a vault is the hands getting ready, not a slide.
  const sliding =
    s.state === S.Slide
      ? tucked && speed0 >= t.crouchSpeed
      : input.tuck && speed0 >= t.slideSpeed && s.vaultArmBox < 0;
  if (sliding) {
    const v0s = s.vx;
    s.facing = sign(v0s);
    const perfect = t.hurdleFlow && s.slideBoostPipe >= 0 && s.slideBoostReady === 0;
    const decel = t.slideDecel * ((perfect ? 0 : 1) + t.slideBrakeBonus * backLean);
    const v = moveToward(v0s, 0, decel * DT);
    s.ax = (v - v0s) / DT;
    s.ay = 0;
    s.vx = v;
    s.groundDistance += abs(v) * DT;
    setState(s, ev, S.Slide);
    return;
  }
  const cap = t.maxRunSpeed + (t.crouchSpeed - t.maxRunSpeed) * s.tuck;
  let target = abs(mx) * t.maxRunSpeed;
  if (target > cap) target = cap;

  const v0 = s.vx;
  let v = v0;
  const speed = abs(v);
  const vdir = sign(v);

  if (dir === 0) {
    // Stop: brake to a standstill.
    const decel = t.stopDecel * (1 + t.leanBrakeBonus * backLean);
    v = moveToward(v, 0, decel * DT);
    if (v === 0) setState(s, ev, S.Idle);
    else if (s.state === S.Skid || speed >= t.skidSpeed) setState(s, ev, S.Skid);
    else setState(s, ev, S.Run);
  } else if (vdir === -dir) {
    // Turnaround: facing flips now, speed reverses through the brake.
    s.facing = dir;
    v = moveToward(v, 0, t.turnDecel * DT);
    if (s.state === S.Skid || speed >= t.skidSpeed) setState(s, ev, S.Skid);
    else setState(s, ev, S.Run);
  } else {
    s.facing = dir;
    if (v === 0 && s.state !== S.Run) {
      // From a standstill (or the zero crossing of a turnaround): the first tick already moves.
      v = t.startImpulse * dir;
      ev.push(Ev.RunStart, s.tick, v);
    }
    const sp = abs(v);
    if (sp < target) {
      const accel = t.runAccel * (1 + t.leanAccelBonus * forwardLean);
      v = moveToward(v, target * dir, accel * DT);
    } else if (sp > t.maxRunSpeed) {
      v = moveToward(v, t.maxRunSpeed * dir, t.overspeedDecel * DT);
    } else if (sp > target) {
      const decel = t.runAccel + (t.stopDecel - t.runAccel) * s.tuck;
      v = moveToward(v, target * dir, decel * DT);
    }
    setState(s, ev, S.Run);
  }

  s.ax = (v - v0) / DT;
  s.ay = 0;
  s.vx = v;
  s.groundDistance += abs(v) * DT;
}
